'use client';

import { fetchAuthPropsOnClient } from '@tuic/auth/src/client';
import { getClientCookies } from '@tuic/lib/src/helpers/cookies';
import { useIsWebviewRequest } from '@tuic/lib/src/hooks/use-is-webview-request';
import Script from 'next/script';
import { useEffect, useMemo } from 'react';

import {
  consentListsAreEqual,
  consentCollectionToConsentList,
  getUtag,
  parseConsentCollectionString,
} from '../core';
import {
  COOKIE_NAME_CONSENT_PRESET,
  setWindowTrackerState,
  TRACKER_LOG,
} from '../utils';
import {
  TRACKER_ENVIRONMENT,
  TRACKER_PROFILE,
  TRACKER_ENABLED,
} from '../utils';
import { logger } from '../utils';
import { EVENT_TRACKER_READY } from '../utils/event-names';

import type { Utag } from '../core';
import type { FC } from 'react';

type TrackerScriptProps = {
  disableTracking?: boolean;
};

export const TrackerScript: FC<TrackerScriptProps> = props => {
  const url = useMemo(() => {
    return `https://d.meinschiff.com/${TRACKER_PROFILE}/${TRACKER_ENVIRONMENT}/utag.js`;
  }, [TRACKER_PROFILE, TRACKER_ENVIRONMENT]);

  const isWebview = useIsWebviewRequest();

  const isLocal =
    process.env.NEXT_PUBLIC_APP_ENV === 'local' ||
    process.env.NEXT_PUBLIC_APP_ENV === 'staging';

  const isEnabled = TRACKER_ENABLED && !props.disableTracking;

  useEffect(() => {
    if (!isLocal) {
      return;
    }

    if (!TRACKER_LOG) {
      // eslint-disable-next-line no-console
      console.info(
        `👀 Tracker log is currently disabled. Do you want to output the tracking log?` +
          `\n🛠️ Set \`NEXT_PUBLIC_TRACKER_LOG=true\` in your environment.`,
      );
    }

    logger.log('Tracker configuration:', {
      enabled: isEnabled,
      profile: TRACKER_PROFILE,
      environment: TRACKER_ENVIRONMENT,
    });
  }, [isLocal]);

  if (!isEnabled) {
    return null;
  }

  const handleTrackerLoad = async () => {
    try {
      handleConsentPreset(getUtag(window));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Could not propagate consent preset.', e);
    }

    await updateTrackerState({
      isWebview,
    });

    logger.log('Tracker is now ready to send data.');

    window.dispatchEvent(new Event(EVENT_TRACKER_READY));
  };

  return (
    <>
      <Script id="tealium-spa-setup">{`
        window.utag_cfg_ovrd = window.utag_cfg_ovrd || {};
        window.utag_cfg_ovrd.noview = true;
      `}</Script>
      <Script
        src={url}
        strategy="afterInteractive"
        async
        onLoad={handleTrackerLoad}
      />
    </>
  );
};

const updateTrackerState = async (args: { isWebview: boolean | undefined }) => {
  const { isWebview } = args;
  const { authenticated } = await fetchAuthPropsOnClient();

  setWindowTrackerState(current => ({
    ...current,
    ready: true,
    meta: {
      ...current.meta,
      userIsSignedIn: authenticated,
      isWebview,
    },
  }));
};

const handleConsentPreset = (utag: Utag | undefined) => {
  if (!document.cookie.includes(`${COOKIE_NAME_CONSENT_PRESET}=`)) {
    logger.log(
      'Consent was not preset: No active consent preset cookie found.',
    );
    return;
  }

  const consentValue = getClientCookies()[COOKIE_NAME_CONSENT_PRESET];

  if (typeof consentValue === 'undefined') {
    logger.log(
      'Consent was not preset: Value of active consent preset cookie is null.',
    );

    return;
  }

  const categories = parseConsentCollectionString(consentValue);

  if (typeof categories === 'undefined') {
    logger.log(
      `Consent was not preset: Consent value string of "${consentValue}" could not be parsed to a categories collection.`,
    );

    return;
  }

  if (!utag) {
    logger.error('Could not set consent preset. Utag not ready');
    return;
  }

  const currentList = utag.gdpr.getSelectedCategories();
  const desiredList = consentCollectionToConsentList(categories);

  const deltaExists = !consentListsAreEqual(currentList, desiredList);

  if (!deltaExists) {
    logger.log(
      `Consent was not preset: Desired consent categories equal the currently set categories.`,
    );
    logger.log('Current consented categories', currentList);
    logger.log('Desired consented categories', desiredList);

    return;
  }

  utag.gdpr.setPreferencesFromList(desiredList);

  logger.log('Consent was preset.');
  logger.log('Previous consented categories', currentList);
  logger.log('New consented categories', desiredList);
};
